/** Endpoints #31-#32 — server-persisted UI state (§4 `ui_state`, §12.4). */
import { z } from 'zod';
import { POSTS_COLUMNS, POSTS_SORT_KEYS } from '../columns.js';
import { BUCKET_GRANULARITIES, CONTENT_TYPES, LENGTH_UNITS, METRIC_KEYS, POST_TYPES, SCHEDULE_HEATMAP_MODES, SORT_DIRS, VIEW_MODES, } from '../enums.js';
import { MAX_PERF_CONDITIONS, PERF_KINDS, RANGE_PRESETS } from './common.js';
import { CONTENT_COMMENTS_COLUMNS, CONTENT_COMMENTS_SORT_KEYS, CONTENT_MODES, CONTENT_POSTS_COLUMNS, CONTENT_POSTS_SORT_KEYS, } from './content.js';
/** Key registry: per-subject view state, one global column layout, one global feed state. */
export const UI_STATE_KEY_PATTERN = /^(?:view:(?:profile|group):[1-9][0-9]*|columns:posts|feed:main|saved:main|watch:main|content:main|columns:content-(?:posts|comments))$/;
export const UiStateKeyParamsSchema = z.object({
    key: z.string().max(100).regex(UI_STATE_KEY_PATTERN, 'unknown ui-state key'),
});
export const POSTS_COLUMNS_UI_STATE_KEY = 'columns:posts';
/** The `/feed` screen's filters/sort/range (#51, FEED_SPEC) — one blob, not per subject. */
export const FEED_UI_STATE_KEY = 'feed:main';
/** The `/saved` screen's filters/sort/range (#66, SPEC `saved-posts`) — one blob, like the feed. */
export const SAVED_UI_STATE_KEY = 'saved:main';
/** The `/watch` screen's filters/sort/range (#71, §19) — the `feed:main` shape, its own blob. */
export const WATCH_UI_STATE_KEY = 'watch:main';
/** The `/content` screen's mode/range/granularity/sorts (#87-#89) — one global blob. */
export const CONTENT_UI_STATE_KEY = 'content:main';
/** Per-table column layouts of `/content`, global like `columns:posts`. */
export const CONTENT_POSTS_COLUMNS_UI_STATE_KEY = 'columns:content-posts';
export const CONTENT_COMMENTS_COLUMNS_UI_STATE_KEY = 'columns:content-comments';
export function viewUiStateKey(type, id) {
    return `view:${type}:${id}`;
}
/* ------------------------------------------------------------ value shapes */
/** Selected filter values (the UI's own representation; the API params are derived from it). */
export const ViewFiltersStateSchema = z.object({
    postTypes: z.array(z.enum(POST_TYPES)).optional(),
    contentTypes: z.array(z.enum(CONTENT_TYPES)).optional(),
    authorIds: z.array(z.number().int().positive()).optional(),
    /**
     * Performance conditions, AND-combined. Blobs written before this shape carry `perfMetric`/
     * `perfMin` instead; the client's sanitizer migrates the pair into one multiplier condition.
     */
    perf: z
        .array(z.object({
        metric: z.enum(METRIC_KEYS),
        kind: z.enum(PERF_KINDS),
        min: z.number().nonnegative(),
    }))
        .max(MAX_PERF_CONDITIONS)
        .optional(),
    lenUnit: z.enum(LENGTH_UNITS).optional(),
    lenMin: z.number().int().nonnegative().optional(),
    lenMax: z.number().int().nonnegative().optional(),
    hasMedia: z.boolean().optional(),
    /** LinkedIn's post-edited marker (§7.4). */
    edited: z.boolean().optional(),
    /** Any outbound link in the post text (§7.4). */
    hasLink: z.boolean().optional(),
    /** Saved-posts membership tri-state (SPEC `saved-posts`); absent = "all". */
    saved: z.boolean().optional(),
    q: z.string().max(200).optional(),
});
export const ViewRangeStateSchema = z.object({
    preset: z.enum(RANGE_PRESETS),
    /** Only meaningful when `preset === 'custom'`. */
    from: z.number().int().optional(),
    to: z.number().int().optional(),
});
/** Value stored at `view:profile:<id>` / `view:group:<id>` (§12.4). */
export const ViewUiStateSchema = z.object({
    mode: z.enum(VIEW_MODES).optional(),
    range: ViewRangeStateSchema.optional(),
    filters: ViewFiltersStateSchema.optional(),
    /** The Posts table's ordering. */
    sort: z
        .object({
        key: z.enum(POSTS_SORT_KEYS),
        dir: z.enum(SORT_DIRS),
    })
        .optional(),
    /**
     * The Feed tab's own ordering — the two tabs sort independently (§12.3). Absent in blobs written
     * before the split, where the client seeds it from `sort` once.
     */
    feedSort: z
        .object({
        key: z.enum(POSTS_SORT_KEYS),
        dir: z.enum(SORT_DIRS),
    })
        .optional(),
    /** Overview bucket-granularity override; absent = server auto-pick per §8.5. */
    granularity: z.enum(BUCKET_GRANULARITIES).optional(),
    /** Schedule calendar face; absent = `volume` (§8.6). Independent of `matrixMode`. */
    calendarMode: z.enum(SCHEDULE_HEATMAP_MODES).optional(),
    /** Schedule day × hour matrix face; absent = `volume` (§8.6). */
    matrixMode: z.enum(SCHEDULE_HEATMAP_MODES).optional(),
});
/**
 * The `/feed` sidebar's selection: the view filter shape minus the two params the global feed has
 * no place for — `q` (search is Posts-only) and `authorIds` (the scope params replace it) — plus
 * the scope itself. Ids that no longer exist are pruned client-side and ignored server-side, so a
 * stale selection is never an error.
 */
export const FeedFiltersStateSchema = ViewFiltersStateSchema.omit({
    q: true,
    authorIds: true,
}).extend({
    groupIds: z.array(z.number().int().positive()).optional(),
    profileIds: z.array(z.number().int().positive()).optional(),
});
/** Value stored at `feed:main` (FEED_SPEC § API). Sort/range are the shared #25 vocabulary. */
export const FeedUiStateSchema = z.object({
    range: ViewRangeStateSchema.optional(),
    filters: FeedFiltersStateSchema.optional(),
    sort: z
        .object({
        key: z.enum(POSTS_SORT_KEYS),
        dir: z.enum(SORT_DIRS),
    })
        .optional(),
});
/**
 * `/watch` is `/feed` narrowed to watch-caught posts (#71), so its sidebar speaks the *same*
 * vocabulary — aliases rather than a second shape, because a divergence between the two would be a
 * bug, not a feature. `q` is session-only here, exactly as on `/saved`, so it is absent from both.
 */
export const WatchFiltersStateSchema = FeedFiltersStateSchema;
/** Value stored at `watch:main` (§12.4). */
export const WatchUiStateSchema = FeedUiStateSchema;
/**
 * The `/saved` sidebar's selection: the view filter vocabulary minus the three params a collection
 * has no place for — `q` (session-only there), `authorIds` (no author scoping) and `saved` itself
 * (everything on the screen is saved) — plus the saved-only narrowings and the `/feed` scope pair.
 * Dangling list/tag/group/profile ids are pruned client-side on load and ignored server-side, so a
 * stale selection is never an error.
 */
export const SavedFiltersStateSchema = ViewFiltersStateSchema.omit({
    q: true,
    authorIds: true,
    saved: true,
}).extend({
    listIds: z.array(z.number().int().positive()).optional(),
    tagIds: z.array(z.number().int().positive()).optional(),
    savedFrom: z.number().int().optional(),
    savedTo: z.number().int().optional(),
    /** The `/feed` scope pair, same shape: a blob written before them simply lacks the keys. */
    groupIds: z.array(z.number().int().positive()).optional(),
    profileIds: z.array(z.number().int().positive()).optional(),
});
/** Value stored at `saved:main` (SPEC `saved-posts` § Data model). */
export const SavedUiStateSchema = z.object({
    range: ViewRangeStateSchema.optional(),
    filters: SavedFiltersStateSchema.optional(),
    sort: z
        .object({
        key: z.enum(POSTS_SORT_KEYS),
        dir: z.enum(SORT_DIRS),
    })
        .optional(),
});
const POSTS_COLUMN_ID_VALUES = POSTS_COLUMNS.map((c) => c.id);
/** Value stored at `columns:posts` — global column layout (§12.3). */
export const PostsColumnsUiStateSchema = z.object({
    /** Full display order; unknown/missing ids fall back to the canonical order. */
    order: z.array(z.enum(POSTS_COLUMN_ID_VALUES)).optional(),
    hidden: z.array(z.enum(POSTS_COLUMN_ID_VALUES)).optional(),
});
/* ---------------------------------------------------------------- content */
const CONTENT_POSTS_COLUMN_ID_VALUES = CONTENT_POSTS_COLUMNS.map((c) => c.id);
const CONTENT_COMMENTS_COLUMN_ID_VALUES = CONTENT_COMMENTS_COLUMNS.map((c) => c.id);
/** Value stored at `content:main` (SPEC `Content Performance` § Frontend). */
export const ContentUiStateSchema = z.object({
    mode: z.enum(CONTENT_MODES).optional(),
    range: ViewRangeStateSchema.optional(),
    /** Applies to all three chart sections; absent = the server default (`week`). */
    granularity: z.enum(BUCKET_GRANULARITIES).optional(),
    postsSort: z
        .object({ key: z.enum(CONTENT_POSTS_SORT_KEYS), dir: z.enum(SORT_DIRS) })
        .optional(),
    commentsSort: z
        .object({ key: z.enum(CONTENT_COMMENTS_SORT_KEYS), dir: z.enum(SORT_DIRS) })
        .optional(),
});
/** Value stored at `columns:content-posts`. */
export const ContentPostsColumnsUiStateSchema = z.object({
    order: z.array(z.enum(CONTENT_POSTS_COLUMN_ID_VALUES)).optional(),
    hidden: z.array(z.enum(CONTENT_POSTS_COLUMN_ID_VALUES)).optional(),
});
/** Value stored at `columns:content-comments`. */
export const ContentCommentsColumnsUiStateSchema = z.object({
    order: z.array(z.enum(CONTENT_COMMENTS_COLUMN_ID_VALUES)).optional(),
    hidden: z.array(z.enum(CONTENT_COMMENTS_COLUMN_ID_VALUES)).optional(),
});
/** #32 `PUT /api/ui-state/:key` — arbitrary JSON, ≤16 KB, `204` on success. */
export const UiStateBodySchema = z.unknown();
//# sourceMappingURL=uiState.js.map